/**
 * WorkspaceStore — the workbench's shared communication layer.
 *
 * Every viewing primitive (editor, board, sidebar, navbar) depends on THIS
 * and nothing else about its siblings. That decoupling is what lets a
 * composition omit, restyle, or replace any one primitive: the editor pushes
 * edits in, the board reads files out, both read/write the shared selection —
 * none of them hold a reference to each other.
 *
 * Same external-store shape as `viewportStore`/`hoverStore` (a closure +
 * `useSyncExternalStore`): synchronous to read, SSR-safe, and re-renders only
 * the subscribers whose slice changed (each slice keeps a stable identity until
 * it itself changes).
 *
 * Base vs working — local experiments on any source, even readonly:
 *   - `base`   = the source's pristine content (the revert target).
 *   - `files`  = the working set the editor/board actually use.
 *   - `dirtyPaths` = where they differ (modified / added / removed).
 * Editing is ALWAYS allowed — `readonly` is a property of the SOURCE (can't be
 * written back), not of the editor. A readonly source just never gets the edits
 * persisted to it; they live here as dirty overlay with per-file `revert`.
 *
 * Incrementality: `edit(path, content)` swaps a single VFS entry and hands a
 * fresh `Map` to subscribers. The board's `useModel`/`IncrementalViewer` diffs
 * that map file-by-file and only `setFile`s the changed one — a keystroke is a
 * per-file delta into the compiler, never a full model resend. The store holds
 * no compiler state; it is the file + UI-state bus feeding the existing pipeline.
 *
 * Beyond files, the store carries the cross-primitive VIEW state a full
 * viewer needs: selection (with sidebar drill-down history + back-nav),
 * blast radius, the active perspective (`mode`), aspect-overlay picks, and
 * the observed board viewport. The viewport is split by frequency: the
 * focal path is reactive state (changes on dive/pop), while the camera
 * (pan/zoom) is a non-notifying accessor (`getLastViewport`) so a 60fps pan
 * never re-renders subscribers. The board stays decoupled through a narrow
 * `ViewportSeam` registered at mount — drill-down history snapshots/restores
 * the camera without the store ever importing the board.
 */
import { useSyncExternalStore } from "react";
import type { TextEdit } from "@archlang/engine";
import type { Vfs } from "@archlang/viewer-core";
import { type Assets, EMPTY_ASSETS } from "../workspaceAssets.js";
import type { WorkspaceSnapshot } from "./filesource/types.js";
// Type-only (erased) — the store persists the view-configurator's per-tab blob
// opaquely; the views layer owns its shape. No runtime import ⇒ no cycle.
import type { ViewConfig } from "../views/clauseModel.js";
// The board hover store is UI-free (a plain closure store) — sharing one per
// workspace links board-item hover ↔ nav-row hover.
import { createHoverStore, type HoverStore } from "../board/hoverStore";
import {
  type Tab,
  type TabKind,
  type TabSpec,
  type TabsSnapshot,
  type PanelKindResolver,
  fileTabPaths,
  makeTab,
  modeForTab,
  reconcileFileTabs,
  reconcileCustomTabs,
  reviveCustomTabs,
  serializeCustomTabs,
  renameInTabs,
  specForPath,
  namespacePersistedTabs,
  OVERVIEW_TAB_ID,
  tabIdForMode,
  tabIdForSpec,
} from "./tabs.js";
import type { RemovedEntityRef } from "./plugins/types.js";

export type { Tab, TabKind, TabSpec, TabsSnapshot } from "./tabs.js";
export { OVERVIEW_TAB_ID, DOC_TAB_ID, TYPES_TAB_ID, NON_FILE_TAB_NS } from "./tabs.js";

/** Shared selection — what the editor, board, and sidebar agree is "current". */
export interface WorkspaceSelection {
  readonly moduleId: string | null;
  readonly edgeId: string | null;
  readonly interfaceKey: string | null;
  /** Process whose steps the board/scrubber are walking, or null. Part of the
   *  selection (not playback state) because picking a process IS a selection
   *  act — it survives module/edge re-picks and is restored by `goBack`. */
  readonly activeProcessId: string | null;
}

/** Input to `applySelection` — module/edge are explicit (a pick names both),
 *  the rest defaults: interfaceKey clears unless given, activeProcessId is
 *  KEPT unless given (pass null to explicitly stop walking a process). */
export interface SelectionInput {
  readonly moduleId: string | null;
  readonly edgeId: string | null;
  readonly interfaceKey?: string | null;
  readonly activeProcessId?: string | null;
}

/** Board viewport as snapshotted into drill-down history / view persistence.
 *  Centre-canonical (size-independent), mirroring the scene `Viewport`. */
export interface BoardViewport {
  readonly focalPath: readonly string[];
  readonly center: { readonly x: number; readonly y: number };
  readonly zoom: number;
}

/**
 * Narrow seam to the mounted board: snapshot the live camera when a
 * drill-down pushes history, restore it when `goBack` pops. Registered by
 * the board primitive on mount (`setViewportSeam`) — the store never holds
 * a board reference, so compositions without a board still work (history
 * entries just carry a null viewport).
 */
export interface ViewportSeam {
  readonly getViewport: () => BoardViewport | null;
  readonly restoreViewport: (vp: BoardViewport) => void;
}

/** One drill-down step the user can back out of: the selection that was
 *  current before the drill, plus the camera to fly back to. */
export interface SelectionHistoryEntry {
  readonly selection: WorkspaceSelection;
  readonly viewport: BoardViewport | null;
}

/** Top-level perspective over the same workspace data. */
export type WorkspaceMode = "board" | "types" | "process" | "docs" | "views";

/**
 * Aspect-overlay picks shared between the HUD dropdown, the board tinting,
 * and the legend. Derivations (legend rows, member highlight, overlay
 * nodes) stay model-side in `useAspectOverlay` — the store is model-free
 * and only carries the user's choices.
 */
export interface WorkspaceAspectState {
  /** Active "colour by" label key, or null when off. */
  readonly colorKey: string | null;
  /** Legend hover previews a value's members (transient)… */
  readonly legendHover: string | null;
  /** …click pins them (sticky). Effective highlight = hover over pin. */
  readonly legendPinned: string | null;
  /** Label keys rendered as an aspect overlay (web hub OR replicated). */
  readonly webKeys: ReadonlySet<string>;
  /** Keys (⊆ webKeys) rendered in `replicated` mode (per-member duplicates)
   *  instead of the shared `web` hub. */
  readonly replicatedKeys: ReadonlySet<string>;
}

/** What `persistView` receives — the restorable view state of a session. */
export interface WorkspaceViewSnapshot {
  readonly viewport: BoardViewport | null;
  readonly selection: WorkspaceSelection;
  /** The full universal-tab snapshot (open tabs + active + per-tab state).
   *  Rides the VIEW channel — tab changes are view state, not file edits. The
   *  perspective ("which pane") is derived from the active tab's kind, so there
   *  is no separate `mode` to persist. */
  readonly tabs: readonly Tab[];
  readonly activeTabId: string | null;
}

export interface WorkspaceState {
  /** Pristine content from the source — the revert target. */
  readonly base: Vfs;
  /** Working set the primitives read. May diverge from `base` (dirty). */
  readonly files: Vfs;
  /** Binary workspace assets (images), keyed by path. Read by the document
   *  surface to resolve relative `<img>` srcs; not part of the dirty/editable
   *  text overlay. Replaced wholesale on each source load. */
  readonly assets: Assets;
  /** Ordered, heterogeneous list of open tabs (board / file / doc / process /
   *  types) — the single source of truth for what is open and showing. The
   *  legacy `mode` / `openTabs` / `activeFile` fields below are DERIVED mirrors
   *  of this list (see `tabs.ts`), kept for primitives/tests that read them. */
  readonly tabs: readonly Tab[];
  /** Id of the active tab (the one whose view fills the centre). */
  readonly activeTabId: string | null;
  /** DERIVED: the active file tab's path, else the last file tab touched (so
   *  the editor/LSP keeps a file context even while a board tab is active). */
  readonly activeFile: string | null;
  /** DERIVED: paths of the file-kind tabs, in order (drives EditorTabs/legacy
   *  file-tab call sites). */
  readonly openTabs: readonly string[];
  readonly selection: WorkspaceSelection;
  /** Modules highlighted as the blast radius of the selection (or a
   *  diagnostic). Cleared on every selection change. */
  readonly blastRadius: readonly string[];
  /** Type selected in the Types view — highlights its instances on the board. */
  readonly selectedTypeName: string | null;
  /** File row selected in the nav (single click). Pure nav highlight — does NOT
   *  open a tab or the editor; double-click opens. */
  readonly selectedFile: string | null;
  /** Sidebar drill-down back-stack (oldest first). `goBack` pops the last
   *  entry and restores its selection + viewport. */
  readonly navHistory: readonly SelectionHistoryEntry[];
  readonly aspect: WorkspaceAspectState;
  /** Process-playback scrubber position for the active tab (mirror of its
   *  `TabViewState.process`). The active process id itself rides `selection`. */
  readonly process: { readonly stepIndex: number | null; readonly autoPlay: boolean };
  /** Focal space path observed from the board (reactive — changes on
   *  dive/pop only; the high-frequency camera lives in `getLastViewport`). */
  readonly focalPath: readonly string[];
  /** Paths where `files` differs from `base` (modified / added / removed). */
  readonly dirtyPaths: ReadonlySet<string>;
  /** Source can't be written back. Editing is still allowed (dirty overlay). */
  readonly readonly: boolean;
  /** Board double-click → scroll the nav to this module's row. Nonce-gated
   *  so repeat opens of the same module still scroll. */
  readonly navReveal: { readonly key: string; readonly nonce: number } | null;
  /** Pending "jump the editor to this code location" request. DURABLE state
   *  (not a transient event) so it survives the editor MOUNTING: opening a file
   *  from a board tab mounts the editor column on the same tick the reveal is
   *  requested, so a fire-once event would be emitted before the editor
   *  subscribes and be lost. A freshly-mounted editor reads this slice and
   *  applies it once its Monaco model for `path` is ready. Nonce-gated so repeat
   *  jumps to the same spot still fire. */
  readonly reveal: {
    readonly path: string;
    readonly line: number;
    readonly column: number;
    readonly focus: boolean;
    readonly nonce: number;
  } | null;
}

/**
 * Cross-primitive signals on the event bus. Two flavours:
 *  - file mutations (created/changed/removed/renamed) — what the persistence
 *    binder maps to the FileSource's granular methods. Distinct events so a
 *    rename reaches a server as a rename (keeping identity/history), not a
 *    remove + write.
 * These are NOT state (they don't linger or replay on re-subscribe).
 *
 * The editor-jump signal exists in BOTH forms: the durable `reveal` state slice
 * + `requestReveal` (preferred — survives the editor MOUNTING after the
 * request, e.g. opening a file from a board tab) AND this transient `reveal`
 * event (legacy — fine when the editor is already mounted, as in Studio's
 * always-split editor). The Editor consumes both; `requestReveal` is the one to
 * reach for in new code.
 *
 * Note: external changes applied via `applyExternal` deliberately emit NOTHING,
 * so an inbound server/collab change can't echo back out through the binder.
 */
export type WorkspaceEvent =
  | { readonly type: "fileCreated"; readonly path: string; readonly content: string }
  | {
      readonly type: "fileChanged";
      readonly path: string;
      readonly content: string;
      /** The span-anchored edits that produced `content`, when the write came
       *  through `applyEditsToFile` (a board gesture / LSP edit) rather than a
       *  raw keystroke. Offsets are UTF-16 against the text BEFORE them. The
       *  binder forwards these to a source implementing `writeEdits`, so an IDE
       *  host splices its own buffer instead of overwriting it. */
      readonly edits?: readonly TextEdit[];
    }
  | {
      readonly type: "reveal";
      readonly path: string;
      readonly line?: number;
      readonly column?: number;
      readonly focus?: boolean;
    }
  | { readonly type: "fileRemoved"; readonly path: string }
  | {
      readonly type: "fileRenamed";
      readonly from: string;
      readonly to: string;
      readonly content: string;
      /** Exact old→new TAB-id pairs the rename re-keyed (the exact-match tab
       *  plus its clones) — listeners migrating per-tab session state use
       *  this, never an id-shape guess ('#' is legal in file names). */
      readonly renamedTabIds: ReadonlyArray<readonly [string, string]>;
    }
  /** The editor's text selection changed (editor → board). For a non-empty
   *  selection, carries the covered line range (1-based, inclusive) in `path`
   *  and `cleared: false` — drives the camera operator's "frame what I selected
   *  in code". When the selection collapses to a bare cursor, `cleared: true`
   *  (range ignored) — drives the operator's drift-back. */
  | {
      readonly type: "editorSelection";
      readonly path: string;
      readonly startLine: number;
      readonly endLine: number;
      /** 1-based columns (Monaco convention: end is AFTER the last selected
       *  char). Lets listeners distinguish one selected word from the whole
       *  line — e.g. framing one step endpoint vs both. */
      readonly startColumn: number;
      readonly endColumn: number;
      readonly cleared: boolean;
      /** True when the selection came from the pointer (Monaco source "mouse")
       *  AND covers exactly one bare identifier — the double-click-a-word shape.
       *  The operator suppresses framing for these when they resolve only to a
       *  referenced module, so copying a word doesn't fly the camera. */
      readonly wordLike?: boolean;
    }
  /** A bulk workspace (re)load (replaceFiles / hydrate) — listeners that cache
   *  workspace-level state (e.g. the LSP worker) should fully reseed, not diff.
   *  `workspaceReplaced` marks a COLD load (a different workspace/changeset —
   *  per-tab view records were reset): session state keyed by deterministic
   *  tab ids (which survive the swap) must drop; a warm re-hydrate (background
   *  refetch of the SAME workspace) keeps it. */
  | { readonly type: "reload"; readonly workspaceReplaced?: boolean }
  /** Nav double-click → fly the board to a module (nav → board). */
  | { readonly type: "boardFly"; readonly moduleId: string }
  /** Frame two endpoint nodes together (process scrubber's "locate step"). */
  | { readonly type: "framePair"; readonly aId: string; readonly bId: string };

const EMPTY_SELECTION: WorkspaceSelection = {
  moduleId: null,
  edgeId: null,
  interfaceKey: null,
  activeProcessId: null,
};
const EMPTY_BLAST: readonly string[] = [];
const EMPTY_HISTORY: readonly SelectionHistoryEntry[] = [];
const EMPTY_ASPECT: WorkspaceAspectState = {
  colorKey: null,
  legendHover: null,
  legendPinned: null,
  webKeys: new Set(),
  replicatedKeys: new Set(),
};
/** A long drill-down shouldn't grow the back-stack unbounded. */
const MAX_NAV_HISTORY = 50;

/**
 * The per-tab VIEW state — everything a tab is "looking at" that used to be a
 * global singleton on the store. Each open tab owns one of these, keyed by tab
 * id in `views` (see `createWorkspaceStore`); the matching `WorkspaceState`
 * fields (`selection` / `navHistory` / `blastRadius` / `aspect` / …) are MIRRORS
 * of the ACTIVE tab's record, projected on every tab switch — the same
 * derived-mirror pattern `deriveMirrors` already uses for `activeFile`/`mode`.
 * So switching tabs swaps the whole view bundle atomically, and the nav (which
 * reads the mirrors) follows the active tab for free.
 *
 * Tab IDENTITY/input (path, rootId, processId, doc name, typeName) stays on the
 * `Tab` object in `tabs.ts`; only ephemeral VIEW state lives here.
 */
export interface TabViewState {
  readonly selection: WorkspaceSelection;
  readonly navHistory: readonly SelectionHistoryEntry[];
  readonly blastRadius: readonly string[];
  readonly aspect: WorkspaceAspectState;
  /** Process-playback position (the active process id itself rides
   *  `selection.activeProcessId`). `stepIndex` null = scrubber at the start. */
  readonly process: { readonly stepIndex: number | null; readonly autoPlay: boolean };
  /** The view configurator's per-tab state (null = no config on this tab). NOT a
   *  `mirrorOf` field — read directly via `getBuilderConfig`, never folded into
   *  `WorkspaceState`. Persisted per tab across reloads. */
  readonly builderConfig: ViewConfig | null;
}

/** One serialized per-tab view record (tab id → its `TabViewState`). The wire
 *  shape for `initialViews` / view persistence. */
export interface TabViewEntry {
  readonly id: string;
  readonly view: TabViewState;
}

const DEFAULT_TAB_VIEW: TabViewState = {
  selection: EMPTY_SELECTION,
  navHistory: EMPTY_HISTORY,
  blastRadius: EMPTY_BLAST,
  aspect: EMPTY_ASPECT,
  process: { stepIndex: null, autoPlay: false },
  builderConfig: null,
};

/**
 * A process tab CARRIES its process (`tab.state.processId` — that's its tab
 * identity AND its title), while `activeProcessId` is per-tab VIEW state. A
 * record holding a null `activeProcessId` on such a tab is therefore not a
 * state the tab can be in: it renders the empty "Pick a process from the
 * workspace panel." prompt under the process's own title, and cannot self-heal
 * (a re-open dedups to the same tab id and `applyTabs` early-returns). Pinning
 * the tab's identity over a null on EVERY record read/commit fixes it at the
 * root, so every producer of a null — a restored view snapshot, the nav's
 * toggle-off single click that precedes a re-open double click, a deselect —
 * still lands on a seeded process tab.
 */
function pinProcessIdentity(view: TabViewState, tab: Tab | undefined): TabViewState {
  if (view.selection.activeProcessId !== null) return view;
  if (tab?.kind !== "process" || !tab.state.processId) return view;
  return {
    ...view,
    selection: { ...view.selection, activeProcessId: tab.state.processId },
  };
}

function selectionEquals(a: WorkspaceSelection, b: WorkspaceSelection): boolean {
  return (
    a.moduleId === b.moduleId &&
    a.edgeId === b.edgeId &&
    a.interfaceKey === b.interfaceKey &&
    a.activeProcessId === b.activeProcessId
  );
}

export interface WorkspaceStore {
  /** Synchronous current state — always consistent with `subscribe`. */
  getState(): WorkspaceState;
  subscribe(onChange: () => void): () => void;

  // --- files ---------------------------------------------------------------
  /** Replace one file's content (the per-keystroke path). No-op if unchanged.
   *  `edits` is the span-anchored batch that produced `content` when the caller
   *  has one (`applyEditsToFile`); it rides the `fileChanged` event out to a
   *  source that can splice rather than overwrite. */
  edit(path: string, content: string, edits?: readonly TextEdit[]): void;
  createFile(path: string, content?: string): void;
  deleteFile(path: string): void;
  /** Renames `from` → `to`. Returns false when REFUSED (target taken, or it is
   *  a directory / lives under a file) so a caller that already mutated other
   *  state can back out. */
  renameFile(from: string, to: string): boolean;
  /** Bulk-load: set both base and working from a snapshot (fresh source load).
   *  `assets` carries binary workspace files (images); omit to clear them. */
  replaceFiles(files: Vfs, activeFile?: string | null, assets?: Assets): void;
  /** Seed base + (optionally diverging) working — for an overlay on a source.
   *  `tabs`/`activeTabId` (the universal-tab snapshot) win over `openTabs` when
   *  present; `openTabs` remains for legacy/back-compat overlay blobs. */
  hydrate(input: {
    base: Vfs;
    working?: Vfs;
    activeFile?: string | null;
    assets?: Assets;
    openTabs?: readonly string[];
    tabs?: readonly Tab[];
    activeTabId?: string | null;
    /** Keep the store's current (boot-seeded / restored) tab list instead of
     *  reseeding — just reconcile it to the new files and ensure file tabs for
     *  `openTabs`. Used by the provider's boot seed so view-persisted board/doc
     *  tabs survive the initial file load. */
    preserveTabs?: boolean;
  }): void;
  /** Apply an external change (server push / collab) to base AND working so it
   *  is not dirty — and emits NO event, so it never echoes back to the source. */
  applyExternal(path: string, content: string | null): void;
  /** Set the active file. Opens it as a file tab if not already open (thin
   *  wrapper over `openTab({ kind: "file", path })`). */
  setActiveFile(path: string | null): void;

  // --- tabs ----------------------------------------------------------------
  /** Open a tab (or activate the existing one with the same identity). Returns
   *  the resolved tab id. Subsumes setActiveFile / open-doc / open-process. */
  openTab(spec: TabSpec): string;
  /** Install the custom-tab (`panelKinds`) resolver after construction — for an
   *  external store created outside the plugin host (Studio), so its custom
   *  tabs get real `idFor`/serialize/revive instead of the NOOP default. */
  setPanelKindResolver(resolve: PanelKindResolver): void;
  /** Activate an already-open tab by id. No-op if already active or unknown. */
  activateTab(id: string): void;
  /** Close a tab by id (a file tab's id is its path). If it was active,
   *  activates a neighbour (or null). The underlying file stays in the
   *  workspace; only the tab closes. */
  closeTab(id: string): void;
  /** Move a tab to another tab's position (ids; a file tab's id is its path). */
  reorderTabs(from: string, to: string): void;
  /** Clone a tab into an independent copy (its own id + camera), inserted right
   *  after the source and activated. Returns the new id (null if not found).
   *  Meaningful for board tabs — two views of the model with separate cameras. */
  duplicateTab(id: string): string | null;
  /** Close every tab except `id` (activates it). */
  closeOtherTabs(id: string): void;
  /** Shallow-merge into a tab's per-tab state (board camera/root, doc anchor). */
  updateTabState(id: string, partial: Record<string, unknown>): void;
  /** Close custom (plugin) tabs invalidated by a recompile: each removed model
   *  entity is offered to every custom tab's PanelKind `onEntityRemoved`, and a
   *  `"close"` verdict drops the tab (re-pointing active). The composition
   *  computes `removed` by diffing the model; the store owns the tab list. */
  reconcileCustomTabs(removed: readonly RemovedEntityRef[]): void;

  // --- dirty / revert ------------------------------------------------------
  /** Restore one file to its `base` content (or remove it if not in base). */
  revert(path: string): void;
  /** Restore the whole working set to `base`. */
  revertAll(): void;
  /** Advance `base` to match `files` for the given paths (all if omitted).
   *  Called after a successful write-back so those files are no longer dirty. */
  markSaved(paths?: readonly string[]): void;

  // --- selection -----------------------------------------------------------
  /** History-NEUTRAL merge — primitives that just sync state (editor caret,
   *  nav row) use this; it never touches the drill-down stack. */
  select(next: Partial<WorkspaceSelection>): void;
  /** History-AWARE pick. `source: "board"` = a fresh main-view pick that
   *  resets the drill stack (it becomes the state `goBack` returns to);
   *  `source: "sidebar"` = a drill-down that pushes the prior selection +
   *  viewport so `goBack` can restore both. */
  applySelection(next: SelectionInput, source: "board" | "sidebar"): void;
  /** Pop the drill-down stack: restore that selection and fly the board
   *  back to the snapshotted viewport (when a seam is registered). */
  goBack(): void;
  setBlastRadius(ids: readonly string[]): void;
  /** Select a type in the Types view (null clears). Highlight-only — never
   *  activates a tab, and never rides to a LATER types-tab activation. */
  setSelectedTypeName(name: string | null): void;
  /** OPEN the Types perspective onto `name` (palette / nav double-click): the
   *  pick rides to the types tab this call activates — retagging it — whether
   *  that tab is fresh, an existing background one, or already active. The
   *  open-intent counterpart of the highlight-only `setSelectedTypeName`. */
  openType(name: string): void;
  /** Select a file row in the nav (single click) — highlight only, no tab. */
  setSelectedFile(path: string | null): void;
  clearSelection(): void;
  /** Ask the nav to scroll this module's row into view (board double-click). */
  requestNavReveal(key: string): void;
  /** Ask the editor to jump to a code location (line/column 1-based). `focus`
   *  pulls keyboard focus into the editor (an explicit "take me to this code"
   *  jump) vs leaving focus on the board (a passive reveal). Durable: applied
   *  even if the editor mounts AFTER the request (opening a file from a board
   *  tab). Does NOT open the tab — callers that need the tab open should call
   *  `openTab` first. */
  requestReveal(req: {
    path: string;
    line?: number;
    column?: number;
    focus?: boolean;
  }): void;
  /** Register the mounted board's viewport accessor pair. Returns an
   *  unregister; a later registration simply replaces an earlier one. */
  setViewportSeam(seam: ViewportSeam | null): () => void;

  // --- perspective / aspect overlay -----------------------------------------
  setMode(mode: WorkspaceMode): void;
  /** Single-tab mode (mobile): there is no tab strip, so only the active tab
   *  renders. `state.tabs` stays the full logical set either way — the flag
   *  only relaxes desktop-only guards (the Overview close protection). */
  setSingleTab(on: boolean): void;
  /** Set the "colour by" key. Clears legend hover/pin — they referred to
   *  values of the previous key. */
  setAspectColorKey(key: string | null): void;
  setLegendHover(value: string | null): void;
  setLegendPinned(value: string | null): void;
  toggleWebKey(key: string): void;
  /** Replace the web-key set (model-driven pruning of stale keys). No-op
   *  when the content is identical, so effects can call it idempotently. */
  setWebKeys(keys: ReadonlySet<string>): void;
  /** Set an aspect key's overlay mode: `off` (neither set), `web` (hub +
   *  spokes), or `replicated` (per-member duplicates). Updates `webKeys` and
   *  `replicatedKeys` together — `replicated` ⇒ the key is also in `webKeys`. */
  setAspectMode(key: string, mode: "off" | "web" | "replicated"): void;

  // --- process playback (per-tab scrubber position) --------------------------
  /** Set the active tab's scrubber step (value or updater). null = start. */
  setProcessStep(idx: number | null | ((prev: number | null) => number | null)): void;
  /** Toggle the active tab's process auto-play. */
  setProcessAutoPlay(on: boolean): void;

  // --- viewport observation --------------------------------------------------
  /** Board → store, on every viewport change. Updates the reactive
   *  `focalPath` only when the path actually changed; the camera is kept
   *  out of reactive state (read it via `getLastViewport`). */
  reportViewport(vp: BoardViewport): void;
  /** Latest observed viewport (high-frequency; deliberately non-reactive). */
  getLastViewport(): BoardViewport | null;
  /** All per-tab VIEW records as serializable entries (for view persistence).
   *  The active tab's record is always current here. */
  getViews(): readonly TabViewEntry[];
  /** Every live tab id — the liveness set per-tab session state must be
   *  pruned against (the same set `pruneViews` honors). Equals
   *  `getState().tabs` ids: `state.tabs` is the full logical set even in
   *  single-tab (mobile) mode, which is a render-time projection only. */
  getLiveTabIds(): readonly string[];
  /** The full tab set for persistence. Equals `getState().tabs` — kept as
   *  the persistence-facing accessor (callers shouldn't care how single-tab
   *  mode is represented). */
  getLogicalTabs(): readonly Tab[];

  /** The view configurator's per-tab config for `tabId` (null when none). */
  getBuilderConfig(tabId: string | null): ViewConfig | null;
  /** Set/clear a tab's view-configurator config (persisted per tab). */
  setBuilderConfig(tabId: string | null, config: ViewConfig | null): void;

  /** Shared board ↔ nav hover channel (one store per workspace). */
  readonly hover: HoverStore;

  // --- transient events ----------------------------------------------------
  emit(ev: WorkspaceEvent): void;
  on(handler: (ev: WorkspaceEvent) => void): () => void;

  /** Persist any pending (debounced) save NOW. Call on pagehide so a fast close
   *  doesn't drop the last edit. No-op when nothing is pending. */
  flush(): void;
  /** Flush, then release the persist timer. Call on (real) provider unmount. */
  destroy(): void;
}

export interface WorkspaceStoreOptions {
  readonly initial?: WorkspaceSnapshot;
  /** Source can't be written back (editing still allowed). Defaults to false. */
  readonly readonly?: boolean;
  /**
   * Called (debounced) whenever the working files change — wire a localStorage
   * overlay writer here. The store stays IO-free. Omit for no persistence.
   */
  readonly persist?: (snapshot: WorkspaceSnapshot) => void;
  /** Debounce window for `persist`. Defaults to 400ms. */
  readonly persistDebounceMs?: number;
  /** Seed the selection (e.g. restored from view persistence). */
  readonly initialSelection?: Partial<WorkspaceSelection>;
  readonly initialMode?: WorkspaceMode;
  /** Seed the universal-tab list (restored from view persistence). Wins over
   *  `initialMode`/`activeFile` seeding when present; reconciled to the files
   *  that actually exist at boot. */
  readonly initialTabs?: readonly Tab[];
  readonly initialActiveTabId?: string | null;
  /** Seed the per-tab VIEW records (restored from view persistence), keyed by
   *  tab id — the per-tab counterpart of `initialSelection`/`initialAspect`.
   *  The active tab's entry wins over the legacy single-value seeds. */
  readonly initialViews?: readonly TabViewEntry[];
  /** Seed aspect picks (the colour/web prefs survive reloads). */
  readonly initialAspect?: {
    readonly colorKey?: string | null;
    readonly webKeys?: readonly string[];
    readonly replicatedKeys?: readonly string[];
  };
  /** Seed the observed viewport (and its focal path) from persistence. */
  readonly initialViewport?: BoardViewport | null;
  /**
   * Called (debounced) whenever the restorable VIEW state changes —
   * viewport, selection, or mode. Wire the viewer-state localStorage writer
   * here. Separate from `persist` because view state changes far more often
   * than files and persists to a different place.
   */
  readonly persistView?: (view: WorkspaceViewSnapshot) => void;
  /** Debounce window for `persistView`. Defaults to 200ms. */
  readonly persistViewDebounceMs?: number;
  /**
   * Resolve a custom (plugin) tab kind to its {@link PanelKind}. The store's
   * `custom` tab arm delegates id-derivation, reload-revive, persist-serialize,
   * and entity-removal to it. Omit (the viewer, which mounts no plugins) → every
   * custom tab drops on reload; Studio passes a registry-backed resolver.
   */
  readonly panelKinds?: PanelKindResolver;
}

// Paths where `files` differs from `base`: modified, added (not in base), or
// removed (in base, gone from files).
function computeDirty(base: Vfs, files: Vfs): ReadonlySet<string> {
  const dirty = new Set<string>();
  for (const [path, content] of files) {
    if (base.get(path) !== content) dirty.add(path);
  }
  for (const path of base.keys()) {
    if (!files.has(path)) dirty.add(path);
  }
  return dirty;
}

function firstPath(files: Vfs | undefined): string | undefined {
  if (!files) return undefined;
  for (const path of files.keys()) return path;
  return undefined;
}

// specForPath (the `.md → doc` routing law) lives in tabs.ts, next to
// renameInTabs — the other site that must re-evaluate a path's tab kind.

// Replacement active-file path after `path` leaves the workspace: a
// neighbouring open file tab (left, else right), else any remaining file.
// `files` must already exclude `path`. Mirrors the historical invariant that
// `activeFile` keeps pointing at SOME file while the workspace is non-empty.
function pickReplacementFile(
  openTabs: readonly string[],
  files: Vfs,
  path: string,
): string | null {
  const idx = openTabs.indexOf(path);
  const remaining = openTabs.filter((p) => p !== path && files.has(p));
  return remaining[idx - 1] ?? remaining[idx] ?? remaining[0] ?? firstPath(files) ?? null;
}

// Seed the universal-tab list from a fresh source load: a single overview
// board tab + one file tab per open path, active = the active file's tab (else
// the board). When the restored mode is "types" (a singleton view with no file
// identity), seed + activate a types tab so the perspective is preserved; a
// "process" mode restores onto the restored selection's process when there is
// one. Doc mode (and a process mode with no restored process) needs entity
// identity we don't have at seed time, so it falls back to the board/file tab.
function seedTabs(
  activeFile: string | null,
  openTabs: readonly string[] | undefined,
  mode: WorkspaceMode,
  activeProcessId: string | null = null,
): { tabs: readonly Tab[]; activeTabId: string | null } {
  const board = makeTab({ kind: "board" });
  const paths = openTabs ?? (activeFile ? [activeFile] : []);
  // Dedup by tab id: several `.md` paths all resolve to the DOC singleton —
  // minting one tab per path corrupted the list with duplicate ids. The
  // singleton shows the ACTIVE `.md` (else the first one seen).
  const activeSpec = activeFile ? specForPath(activeFile) : null;
  const fileTabs: Tab[] = [];
  const seen = new Set<string>();
  for (const path of paths) {
    const spec = specForPath(path);
    const id = tabIdForSpec(spec);
    if (seen.has(id)) continue;
    seen.add(id);
    fileTabs.push(makeTab(spec.kind === "doc" && activeSpec?.kind === "doc" ? activeSpec : spec));
  }
  const tabs: Tab[] = [board, ...fileTabs];
  const activeFileTabId = activeSpec ? tabIdForSpec(activeSpec) : null;
  let activeTabId: string;
  if (mode === "types" || (mode === "process" && activeProcessId)) {
    // Restore a non-board perspective with a singleton/entity view tab. The
    // process arm needs the restored selection's process — an empty-id process
    // tab is a dead tab (see setMode), so with no process to restore the
    // perspective falls back to the board/file tab instead.
    const view =
      mode === "types"
        ? makeTab({ kind: "types" })
        : makeTab({ kind: "process", processId: activeProcessId! });
    tabs.push(view);
    activeTabId = view.id;
  } else if (activeFileTabId && fileTabs.some((t) => t.id === activeFileTabId)) {
    activeTabId = activeFileTabId;
  } else {
    activeTabId = board.id;
  }
  return { tabs, activeTabId };
}

/**
 * Derive the `selectedTypeName` view-selection slice from the active tab. `mode`
 * is a derived mirror of the active tab's kind, but the Types surface also reads
 * this slice to know WHICH type to show. On a fresh boot from a restored tab
 * snapshot the active tab can already be a types tab, so it must be seeded from
 * it — otherwise the perspective restores but the surface is empty. Returns `{}`
 * for tab kinds that don't drive this slice, leaving it as-is.
 */
function deriveViewSelectionFromTab(
  activeTab: Tab | undefined,
): { selectedTypeName?: string | null } {
  if (activeTab?.kind === "types") {
    // A types tab's OWN typeName (possibly null for a fresh tab) IS the slice —
    // a fresh tab must clear a stale global pick left by a closed types tab,
    // else it RENDERS the dead tab's type while PERSISTING null.
    return { selectedTypeName: activeTab.state.typeName ?? null };
  }
  return {};
}

export function createWorkspaceStore(
  opts: WorkspaceStoreOptions = {},
): WorkspaceStore {
  const {
    persist,
    persistDebounceMs = 400,
    readonly = false,
    persistView,
    persistViewDebounceMs = 200,
  } = opts;

  // Custom-tab kind resolver — omitted (viewer) → a NOOP that resolves nothing,
  // so custom tabs can't revive and drop on reload. Mutable so an EXTERNAL store
  // (Studio creates its own, outside the plugin host) can be handed the
  // registry resolver after mount via `setPanelKindResolver` (read at call time
  // by openTab/serialize/reconcile below).
  let resolvePanelKind: PanelKindResolver = opts.panelKinds ?? (() => undefined);

  const initialFiles: Vfs = opts.initial?.files ?? new Map<string, string>();
  // No explicit `activeFile` means no file should be open — a fresh boot
  // activates the board, never an arbitrarily-picked first VFS entry.
  const initialActive = opts.initial?.activeFile ?? null;
  const initialMode = opts.initialMode ?? "board";
  // A restored tab snapshot (from view persistence) wins over mode/active-file
  // seeding — reconciled to the files that exist at boot, then custom tabs
  // revived (unresolved kind / revive→null are dropped, apiVersion-style).
  // Non-file ids minted before the NUL namespace are migrated first (tabs +
  // active pointer + view-record keys below), so an old persisted "board:…"
  // overview still dedups against the ids this build mints.
  const migrated = opts.initialTabs ? namespacePersistedTabs(opts.initialTabs) : null;
  const migratedId = (id: string | null | undefined): string | null =>
    id == null ? null : (migrated?.idMap.get(id) ?? id);
  const seeded = migrated
    ? (() => {
        const r = reconcileFileTabs(
          migrated.tabs,
          initialFiles,
          migratedId(opts.initialActiveTabId),
        );
        return reviveCustomTabs(r.tabs, r.activeTabId, resolvePanelKind);
      })()
    : seedTabs(
        initialActive,
        undefined,
        initialMode,
        opts.initialSelection?.activeProcessId ?? null,
      );
  const seededActiveTab = seeded.tabs.find((t) => t.id === seeded.activeTabId);
  // A restored active doc/types tab also seeds the surface's view-selection
  // slice, so the perspective restores onto the right doc/type (not empty).
  const seededViewSelection = deriveViewSelectionFromTab(seededActiveTab);
  // The path of the most recently active FILE tab — the `activeFile` mirror
  // falls back to this while a non-file tab is active, so the editor/LSP keeps
  // its file context across a board/doc/process/types view.
  let lastFilePath: string | null = initialActive;

  // Per-tab VIEW state, keyed by tab id. The active tab's record is MIRRORED
  // into `state.selection`/`navHistory`/`blastRadius`/`aspect` and re-projected
  // on every tab switch (see `projectActiveView`); those fields are derived,
  // never the source of truth. A restored snapshot (`initialViews`) wins;
  // otherwise the active tab's view is seeded from the legacy single-value init
  // options so a restored selection/aspect lands on the tab that was active.
  const views = new Map<string, TabViewState>();
  for (const e of opts.initialViews ?? []) {
    const key = migratedId(e.id)!;
    // Several records can migrate onto ONE survivor id (the doc-law heal
    // merges duplicate-surface blobs). Deterministic rule: the record reached
    // through the ACTIVE tab's id chain wins; otherwise the first one seen —
    // never "whichever the array happened to list last".
    if (views.has(key) && e.id !== opts.initialActiveTabId) continue;
    views.set(key, e.view);
  }
  const seededView: TabViewState = pinProcessIdentity(
    (seeded.activeTabId ? views.get(seeded.activeTabId) : undefined) ?? {
      ...DEFAULT_TAB_VIEW,
      selection: { ...EMPTY_SELECTION, ...opts.initialSelection },
      aspect: {
        colorKey: opts.initialAspect?.colorKey ?? null,
        legendHover: null,
        legendPinned: null,
        webKeys: new Set(opts.initialAspect?.webKeys ?? []),
        replicatedKeys: new Set(opts.initialAspect?.replicatedKeys ?? []),
      },
    },
    seededActiveTab,
  );
  if (seeded.activeTabId) views.set(seeded.activeTabId, seededView);

  let state: WorkspaceState = {
    base: initialFiles,
    files: initialFiles,
    assets: opts.initial?.assets ?? EMPTY_ASSETS,
    tabs: seeded.tabs,
    activeTabId: seeded.activeTabId,
    activeFile: initialActive,
    openTabs: fileTabPaths(seeded.tabs, []),
    selection: seededView.selection,
    blastRadius: seededView.blastRadius,
    selectedTypeName: seededViewSelection.selectedTypeName ?? null,
    selectedFile: null,
    navHistory: seededView.navHistory,
    aspect: seededView.aspect,
    process: seededView.process,
    focalPath: opts.initialViewport?.focalPath ?? [],
    dirtyPaths: new Set(),
    readonly,
    navReveal: null,
    reveal: null,
  };

  const listeners = new Set<() => void>();
  const eventListeners = new Set<(ev: WorkspaceEvent) => void>();
  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  let viewTimer: ReturnType<typeof setTimeout> | undefined;
  // A type picked while NO types tab is active (search palette / sidebar pick,
  // which then calls setMode("types")). The NEXT types-tab activation adopts it
  // — retagging that tab — instead of the tab's own (stale/empty) typeName
  // clobbering the just-made pick via `deriveViewSelectionFromTab`.
  let pendingTypePick: string | null = null;
  // Single-tab mode (mobile): there is no tab strip, so `state.tabs` stays the
  // FULL logical set and only RENDERING shows the lone active tab. The flag
  // exists solely for the behaviors that genuinely differ on mobile (the
  // Overview close guards below). An earlier design collapsed `state.tabs` to
  // [active] and stashed the rest — that split one truth into two lists, and
  // every mutation path that forgot the stash lost hidden-tab state (the
  // r17–r21 bugloop family). One list, no stash: every mutation is
  // hidden-tab-aware by construction.
  let singleTab = false;
  // Drop view records for tabs that no longer exist (no leak on close).
  const pruneViews = (tabs: readonly Tab[]): void => {
    const live = new Set(tabs.map((t) => t.id));
    for (const id of views.keys()) if (!live.has(id)) views.delete(id);
  };
  // The camera is deliberately OUTSIDE reactive state — it changes per
  // pan/zoom frame; notifying on it would re-render whole-state subscribers
  // at 60fps. The reactive mirror is `state.focalPath` (dive/pop only).
  let lastViewport: BoardViewport | null = opts.initialViewport ?? null;
  let viewportSeam: ViewportSeam | null = null;

  const notify = (): void => {
    for (const l of listeners) l();
  };
  const emit = (ev: WorkspaceEvent): void => {
    for (const l of eventListeners) l(ev);
  };

  // ---- Per-tab view records -------------------------------------------------
  // `views` is the source of truth; `state.selection`/`navHistory`/`blastRadius`
  // /`aspect` are MIRRORS of the active tab's record. `viewFor` lazily defaults a
  // tab's record; `commitActiveView` writes the active tab's record AND its
  // mirror in one step; `projectActiveView` re-points the mirror on a tab switch.
  // `tabs` is the list the caller is COMMITTING — state-assembly sites pass
  // their new list explicitly, because inside a `state = { ... }` literal
  // `state.tabs` is still the OLD list (the r22 hydrate bug: a restored
  // active process tab seeded from the pre-hydrate list and missed).
  const viewFor = (id: string | null, tabs: readonly Tab[] = state.tabs): TabViewState =>
    pinProcessIdentity(
      (id !== null ? views.get(id) : undefined) ?? DEFAULT_TAB_VIEW,
      id !== null ? tabs.find((t) => t.id === id) : undefined,
    );

  // The subset of `state` mirrored from a tab's view record.
  const mirrorOf = (
    v: TabViewState,
  ): Pick<
    WorkspaceState,
    "selection" | "navHistory" | "blastRadius" | "aspect" | "process"
  > => ({
    selection: v.selection,
    navHistory: v.navHistory,
    blastRadius: v.blastRadius,
    aspect: v.aspect,
    process: v.process,
  });

  // Mirror fields for whichever tab is active — folded into `state` on every
  // tab switch so the swap is atomic (alongside `deriveMirrors`).
  const projectActiveView = (
    activeTabId: string | null,
    tabs: readonly Tab[] = state.tabs,
  ): Pick<
    WorkspaceState,
    "selection" | "navHistory" | "blastRadius" | "aspect" | "process"
  > => mirrorOf(viewFor(activeTabId, tabs));

  // Patch any tab's view record; re-mirror into `state` only when that tab is
  // the active one (builderConfig is NOT a mirror field, but selection/aspect
  // callers rely on the re-mirror). Returns the updated record.
  const commitTabView = (id: string | null, patch: Partial<TabViewState>): TabViewState => {
    const next: TabViewState = pinProcessIdentity(
      { ...viewFor(id), ...patch },
      id !== null ? state.tabs.find((t) => t.id === id) : undefined,
    );
    if (id !== null) views.set(id, next);
    if (id === state.activeTabId) state = { ...state, ...mirrorOf(next) };
    return next;
  };

  // Patch the ACTIVE tab's view record + re-mirror it into `state`. Returns the
  // updated record. Callers compute the new slice values, then notify/persist.
  const commitActiveView = (patch: Partial<TabViewState>): TabViewState =>
    commitTabView(state.activeTabId, patch);

  const schedulePersist = (): void => {
    if (!persist) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = undefined;
      persist({ files: state.files, activeFile: state.activeFile, openTabs: state.openTabs });
    }, persistDebounceMs);
  };

  const viewSnapshot = (): WorkspaceViewSnapshot => ({
    viewport: lastViewport,
    selection: state.selection,
    // Custom tabs persist their PanelKind-serialized state (built-ins pass
    // through); the symmetric revive runs on reload.
    tabs: serializeCustomTabs(state.tabs, resolvePanelKind),
    activeTabId: state.activeTabId,
  });

  // Compute the derived mirror fields (`activeFile` / `openTabs`) for a given
  // tab list + active id, preserving slice identity when unchanged. (The
  // perspective is derived from the active tab's kind at the render edge, so
  // there is no `mode` mirror here anymore.)
  const deriveMirrors = (
    tabs: readonly Tab[],
    activeTabId: string | null,
  ): { activeFile: string | null; openTabs: readonly string[] } => {
    const activeTab = tabs.find((t) => t.id === activeTabId);
    if (activeTab?.kind === "file") {
      lastFilePath = activeTab.state.path;
    } else if (
      lastFilePath !== null &&
      !tabs.some((t) => t.kind === "file" && t.state.path === lastFilePath)
    ) {
      // The remembered file tab was closed — fall back to any remaining file.
      lastFilePath =
        tabs.find((t): t is Extract<Tab, { kind: "file" }> => t.kind === "file")?.state
          .path ?? null;
    }
    const activeFile = activeTab?.kind === "file" ? activeTab.state.path : lastFilePath;
    return {
      activeFile,
      openTabs: fileTabPaths(tabs, state.openTabs),
    };
  };

  // The path shown by the ACTIVE tab if (and only if) it is a file tab. The
  // `activeFile` mirror is NOT this — it falls back to the last-touched file
  // even while a board/doc/process tab is active, so "was the deleted/reverted
  // file the one on screen?" checks must ask the tab, not the mirror (else
  // deleting a BACKGROUND file hijacks the perspective into a file tab).
  const activeFileTabPath = (): string | null => {
    const t = state.tabs.find((x) => x.id === state.activeTabId);
    return t?.kind === "file" ? t.state.path : null;
  };

  // Commit a new tab list + active id: recompute the derived mirrors, replace
  // state, notify, and persist to the VIEW channel (tab/mode changes are view
  // state, never file edits — so they must not trigger the file-overlay save).
  const applyTabs = (
    tabs: readonly Tab[],
    activeTabId: string | null,
    options: { persist?: boolean } = {},
  ): void => {
    const sameTabs = tabs === state.tabs;
    const sameActive = activeTabId === state.activeTabId;
    if (sameTabs && sameActive) return;
    // Prune view records for tabs that no longer exist (no leak on close).
    if (!sameTabs) pruneViews(tabs);
    // A type picked just before this activation (set-then-setMode flows): the
    // newly-active types tab ADOPTS the pending pick — retagged so persistence
    // carries it — rather than its own stale/empty typeName wiping the pick.
    const activating = tabs.find((t) => t.id === activeTabId);
    const pick = pendingTypePick;
    if (pick !== null && activating?.kind === "types") {
      if (activating.state.typeName !== pick) {
        tabs = tabs.map((t): Tab =>
          t === activating ? { ...t, state: { ...t.state, typeName: pick } } : t,
        );
      }
      pendingTypePick = null;
    }
    // `projectActiveView` below receives `tabs` explicitly, so the (possibly
    // new) active tab's default view seeds from the list being committed.
    state = { ...state, tabs, activeTabId, ...deriveMirrors(tabs, activeTabId) };
    // Re-mirror the (possibly new) active tab's view — the atomic per-tab swap.
    // A types tab also re-projects ITS type into the global slice (two types
    // tabs each keep their own pick; boot restore does the same).
    state = {
      ...state,
      ...projectActiveView(activeTabId, tabs),
      ...deriveViewSelectionFromTab(tabs.find((t) => t.id === activeTabId)),
    };
    notify();
    if (options.persist !== false) scheduleViewPersist();
  };

  const scheduleViewPersist = (): void => {
    if (!persistView) return;
    if (viewTimer) clearTimeout(viewTimer);
    viewTimer = setTimeout(() => {
      viewTimer = undefined;
      persistView(viewSnapshot());
    }, persistViewDebounceMs);
  };

  // Flush BOTH pending (debounced) saves now. Shared by flush/destroy.
  const flushAll = (): void => {
    if (saveTimer !== undefined) {
      clearTimeout(saveTimer);
      saveTimer = undefined;
      persist?.({ files: state.files, activeFile: state.activeFile, openTabs: state.openTabs });
    }
    if (viewTimer !== undefined) {
      clearTimeout(viewTimer);
      viewTimer = undefined;
      persistView?.(viewSnapshot());
    }
  };

  // Open (or activate) the tab for a bare `path` in `tabs`, routed through
  // `specForPath` so a `.md` opens the singleton CM6 doc tab — never a Monaco
  // file tab (the doc law). An existing doc tab pointing at another document
  // is RE-POINTED, mirroring openTab's doc arm.
  const ensurePathTab = (
    tabs: readonly Tab[],
    path: string,
  ): { tabs: readonly Tab[]; id: string } => {
    const spec = specForPath(path);
    const id = tabIdForSpec(spec);
    const existing = tabs.find((t) => t.id === id);
    if (!existing) return { tabs: [...tabs, makeTab(spec)], id };
    if (spec.kind === "doc" && existing.kind === "doc" && existing.state.path !== spec.path) {
      return {
        tabs: tabs.map((t): Tab =>
          t.id === id && t.kind === "doc" ? { ...t, state: { path: spec.path } } : t,
        ),
        id,
      };
    }
    return { tabs, id };
  };

  // Commit a new working map: maintain the file tabs (drop tabs whose file
  // vanished; optionally open+activate a new file/doc tab), recompute dirty +
  // derived mirrors, notify, emit, persist (FILE channel — this is a file edit).
  /** Is `path` occupied by something that is NOT a file at that exact key —
   *  i.e. a directory (some file lives under it), or an ancestor that is itself
   *  a file (nothing can live under a file)?
   *
   *  The nav's inline editor grew this law first (`collides()`), but `duplicate`
   *  and drag-`move` call the store DIRECTLY and never met it, so both could
   *  land a file on a directory's name — after which the tree renders that name
   *  twice, once per role. The store is the one place every caller passes
   *  through, so the law lives here and the nav's copy is now a fast, in-place
   *  echo of it (it also drives the inline red styling, which the store can't).
   */
  const pathBlocked = (path: string): boolean => {
    const bare = path.replace(/^\/+/, "");
    // BOTH halves compare lead-stripped. The ancestor half used to probe the map
    // with the NEW path's lead (`files.has(lead + …)`), so in a workspace that
    // holds both conventions — which is a pinned legal state, and is what an
    // empty-workspace create ("/x", since the nav has no first key to copy)
    // followed by a bare-keyed hydrate produces — a bare `src` never answered
    // `has("/src")` and a file landed under a file anyway.
    const ancestors = new Set<string>();
    for (let i = bare.indexOf("/"); i >= 0; i = bare.indexOf("/", i + 1))
      ancestors.add(bare.slice(0, i));
    for (const key of state.files.keys()) {
      const k = key.replace(/^\/+/, "");
      if (k.startsWith(bare + "/")) return true; // `path` is a directory
      if (ancestors.has(k)) return true; // `path` would live under a FILE
    }
    return false;
  };

  const commitFiles = (
    files: Vfs,
    event: WorkspaceEvent | null,
    opts: { base?: Vfs; openFile?: string } = {},
  ): void => {
    const base = opts.base ?? state.base;
    let tabs = state.tabs;
    let activeTabId = state.activeTabId;
    if (opts.openFile !== undefined) {
      ({ tabs, id: activeTabId } = ensurePathTab(tabs, opts.openFile));
    }
    const recon = reconcileFileTabs(tabs, files, activeTabId);
    // A file edit can change the active tab (opening/closing a file tab); prune
    // dropped tabs' view records and re-mirror whichever tab is now active.
    pruneViews(recon.tabs);
    state = {
      ...state,
      base,
      files,
      tabs: recon.tabs,
      activeTabId: recon.activeTabId,
      ...deriveMirrors(recon.tabs, recon.activeTabId),
      ...projectActiveView(recon.activeTabId, recon.tabs),
      dirtyPaths: computeDirty(base, files),
    };
    notify();
    if (event) emit(event);
    schedulePersist();
  };

  return {
    getState: () => state,
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },

    edit(path, content, edits) {
      if (state.files.get(path) === content) return;
      const files = new Map(state.files);
      files.set(path, content);
      commitFiles(files, { type: "fileChanged", path, content, edits });
      // Editing a preview tab pins it (so it survives the next single-click).
      // VIEW-channel persist stays ON (default): the pin lives in the tab
      // snapshot — suppressing it left an editor-only session persisting the
      // edited tab as preview:true, so a reload restored it transient (the
      // next single-click silently replaced it). Debounced + once per tab
      // (the preview flag only flips false here), so no churn.
      const t = state.tabs.find((tt) => tt.id === path);
      if (t?.kind === "file" && t.state.preview) {
        applyTabs(
          state.tabs.map((tt): Tab =>
            tt.id === path && tt.kind === "file"
              ? { ...tt, state: { ...tt.state, preview: false } }
              : tt,
          ),
          state.activeTabId,
        );
      }
    },

    createFile(path, content = "") {
      // A name that's already taken is not an error the user can see: the nav's
      // create flow returned silently, which read as "the + button did nothing".
      // Reveal the file they named instead — content is never clobbered.
      if (state.files.has(path)) {
        this.setActiveFile(path);
        return;
      }
      if (pathBlocked(path)) return; // a directory's name, or under a file
      const files = new Map(state.files);
      files.set(path, content);
      // Open the new file as a tab and activate it (was the active file before).
      commitFiles(files, { type: "fileCreated", path, content }, { openFile: path });
    },

    deleteFile(path) {
      if (!state.files.has(path)) return;
      const files = new Map(state.files);
      files.delete(path);
      // Only when the deleted file is the one ON SCREEN (active FILE tab) does
      // a neighbour open in its place — deleting a background file must not
      // yank the user out of a board/doc/process tab (the mirrors re-point
      // `activeFile` on their own).
      const openFile =
        activeFileTabPath() === path
          ? (pickReplacementFile(state.openTabs, files, path) ?? undefined)
          : undefined;
      commitFiles(files, { type: "fileRemoved", path }, { openFile });
    },

    renameFile(from, to) {
      // Returns whether the rename APPLIED. Callers that mutate other state
      // first (DocumentsPane rewrites every `[[ref]]` to the new name before
      // renaming) have to know: a silent refusal left those rewrites committed
      // and every reference dangling.
      // No-op on a collision: renaming onto an existing path would silently
      // clobber that file (delete `from`, overwrite `to`). Refuse so the nav
      // commit keeps both files intact.
      if (!state.files.has(from) || from === to || state.files.has(to)) return false;
      // Exact-path collision was covered; a file-vs-DIRECTORY one was not, so a
      // drag-move onto an occupied directory name corrupted the tree.
      if (pathBlocked(to)) return false;
      const files = new Map(state.files);
      const content = files.get(from)!;
      files.delete(from);
      files.set(to, content);
      // renameInTabs returns the EXACT old→new tab-id map alongside the list
      // (kind conversions included) — never guessed from id SHAPE: '#' is
      // legal in file names, so any string predicate was ambiguous between
      // "clone of `from`" and "a real sibling's tab" (r13–r15 bug class).
      // An extension-changing rename re-routes the tab kind through the
      // specForPath law (file↔doc), and may DROP a tab (a Monaco clone whose
      // .md rename collided with the taken doc singleton).
      const { tabs, idMap } = renameInTabs(state.tabs, from, to);
      // The last-touched-file mirror follows too — otherwise deriveMirrors
      // sees a dead path and falls back to the FIRST file tab, silently
      // swapping the companion editor to an unrelated file.
      if (lastFilePath === from) lastFilePath = to;
      const mappedActive =
        (state.activeTabId && idMap.get(state.activeTabId)) ?? state.activeTabId;
      // A dropped active tab (clone → taken singleton) lands on the document
      // it renamed into, else stays put.
      const activeTabId = tabs.some((t) => t.id === mappedActive)
        ? mappedActive
        : (tabs.find((t) => t.kind === "doc")?.id ?? tabs[0]?.id ?? null);
      // Re-key the renamed tabs' view records (selection/aspect survive the
      // rename). TWO-PHASE: collect then apply — deleting and setting in one
      // pass self-clobbered when a destination id equaled another entry's
      // still-unprocessed source id.
      const movedRecs: Array<[string, ReturnType<typeof views.get> & object]> = [];
      for (const [oldId, newId] of idMap) {
        const rec = views.get(oldId);
        if (rec) {
          views.delete(oldId);
          movedRecs.push([newId, rec]);
        }
      }
      for (const [id, rec] of movedRecs) views.set(id, rec);
      // A rename can now DROP tabs (clone → taken doc singleton) — sweep
      // their view records like every other list-shrinking mutation.
      pruneViews(tabs);
      state = {
        ...state,
        files,
        tabs,
        activeTabId,
        ...deriveMirrors(tabs, activeTabId),
        ...projectActiveView(activeTabId, tabs),
        dirtyPaths: computeDirty(state.base, files),
      };
      // One rename event (not delete+add) so a server source keeps file
      // identity. Emitted BEFORE notify (state is already committed): event
      // listeners that MIGRATE per-tab session state keyed by the old id
      // (EditModeProvider) must run before liveness-pruning subscribers see
      // the old id dead and discard it. The exact id map rides along so
      // listeners never re-derive it from id shape.
      emit({ type: "fileRenamed", from, to, content, renamedTabIds: [...idMap] });
      notify();
      schedulePersist();
      // The re-keyed tab list is VIEW-channel state (applyTabs' contract) —
      // without this the persisted snapshot keeps the DEAD old id and the
      // boot-time reconcile drops the renamed tab (camera/pin/active lost).
      scheduleViewPersist();
      return true;
    },

    applyExternal(path, content) {
      // Update base AND working together → not dirty. Emit NOTHING so the
      // write-back binder can't bounce this change back to the source.
      const base = new Map(state.base);
      const files = new Map(state.files);
      if (content === null) {
        base.delete(path);
        files.delete(path);
      } else {
        base.set(path, content);
        files.set(path, content);
      }
      const recon = reconcileFileTabs(state.tabs, files, state.activeTabId);
      pruneViews(recon.tabs);
      state = {
        ...state,
        base,
        files,
        tabs: recon.tabs,
        activeTabId: recon.activeTabId,
        ...deriveMirrors(recon.tabs, recon.activeTabId),
        ...projectActiveView(recon.activeTabId, recon.tabs),
        dirtyPaths: computeDirty(base, files),
      };
      notify();
      schedulePersist();
    },

    replaceFiles(files, activeFile, assets = EMPTY_ASSETS) {
      // Fresh source load: base and working start identical (nothing dirty).
      // Assets are replaced wholesale (omitted → cleared, so stale images from
      // a previous workspace don't linger). Tabs reset to a fresh seed. No
      // explicit `activeFile` means no file should be open (board active).
      const active = activeFile ?? null;
      lastFilePath = active;
      const seed = seedTabs(active, undefined, "board");
      // A fresh workspace discards all per-tab view records; the seeded tabs
      // start at default (empty) views, so re-mirror to avoid a stale selection.
      views.clear();
      state = {
        ...state,
        base: files,
        files,
        assets,
        tabs: seed.tabs,
        activeTabId: seed.activeTabId,
        ...deriveMirrors(seed.tabs, seed.activeTabId),
        ...projectActiveView(seed.activeTabId, seed.tabs),
        dirtyPaths: new Set(),
      };
      notify();
      emit({ type: "reload", workspaceReplaced: true });
      schedulePersist();
    },

    hydrate({ base, working, activeFile, assets = EMPTY_ASSETS, openTabs, tabs, activeTabId, preserveTabs }) {
      const files = working ?? base;
      // No explicit `activeFile` means no file should be open (board active) —
      // never an arbitrarily-picked first VFS entry (the fresh-tab stray-file bug).
      const active = activeFile ?? null;
      lastFilePath = active;
      // Tab restoration precedence:
      //  1. an explicit full snapshot (`tabs`) — use it (reconciled),
      //  2. `preserveTabs` — keep the boot-seeded/restored list, just reconcile
      //     to the new files + ensure file tabs for `openTabs` / the active file,
      //  3. otherwise reseed from the legacy openTabs/activeFile.
      let nextTabs: readonly Tab[];
      let nextActive: string | null;
      if (tabs) {
        // A restored snapshot may predate the non-file id namespace — migrate
        // (idempotent) so the ids dedup against the ones this build mints.
        const mig = namespacePersistedTabs(tabs);
        const recon = reconcileFileTabs(
          mig.tabs,
          files,
          (activeTabId && (mig.idMap.get(activeTabId) ?? activeTabId)) ?? null,
        );
        // Reload path — revive/drop custom tabs (unresolved kind or revive→null
        // are dropped like an apiVersion mismatch).
        const revived = reviveCustomTabs(recon.tabs, recon.activeTabId, resolvePanelKind);
        nextTabs = revived.tabs;
        nextActive = revived.activeTabId;
      } else if (preserveTabs) {
        let merged = state.tabs;
        const wantFiles = openTabs ?? (active ? [active] : []);
        for (const path of wantFiles) {
          const id = tabIdForSpec(specForPath(path));
          if (!merged.some((t) => t.id === id)) merged = [...merged, makeTab(specForPath(path))];
        }
        // The restored/seeded active tab (board/doc/process/types or a pinned
        // file) IS the perspective to keep — that's the whole point of
        // `preserveTabs`. Only fall back to the freshly-loaded active file when
        // the store has no active tab yet (a cold boot with no view state).
        const activeTabIdForFile = active ? tabIdForSpec(specForPath(active)) : null;
        const wantActive =
          state.activeTabId && merged.some((t) => t.id === state.activeTabId)
            ? state.activeTabId
            : activeTabIdForFile && merged.some((t) => t.id === activeTabIdForFile)
              ? activeTabIdForFile
              : state.activeTabId;
        const recon = reconcileFileTabs(merged, files, wantActive);
        nextTabs = recon.tabs;
        nextActive = recon.activeTabId;
      } else {
        const seed = seedTabs(active, openTabs, "board");
        const recon = reconcileFileTabs(seed.tabs, files, seed.activeTabId);
        nextTabs = recon.tabs;
        nextActive = recon.activeTabId;
      }
      // A restored active doc/types tab also seeds the docs/types surface's
      // view-selection so the perspective restores onto the right entity. Only
      // SEED it (never clear an already-set slice) — a board/file tab leaves
      // whatever the user last picked untouched.
      const hydratedViewSelection = deriveViewSelectionFromTab(
        nextTabs.find((t) => t.id === nextActive),
      );
      // `in` (not `??`): an active types tab with typeName null must CLEAR the
      // slice, not fall back to the previous workspace's stale pick.
      const hydratedTypeName =
        "selectedTypeName" in hydratedViewSelection
          ? (hydratedViewSelection.selectedTypeName ?? null)
          : state.selectedTypeName;
      // Per-tab view records across the hydrate: a COLD reseed replaces the
      // workspace (changeset switch) — records must go WHOLESALE (ids are
      // deterministic: the same-path file tab and `board:__root__` survive
      // any reseed, so liveness pruning alone resurrects the previous
      // workspace's selection/config; mirror replaceFiles' views.clear()).
      // A warm re-hydrate or an explicit restored snapshot keeps its own
      // records, pruned to the reconciled tab list.
      if (!preserveTabs && !tabs) views.clear();
      else pruneViews(nextTabs);
      state = {
        ...state,
        base,
        files,
        assets,
        tabs: nextTabs,
        activeTabId: nextActive,
        ...deriveMirrors(nextTabs, nextActive),
        // Re-mirror the active tab's view (preserved when the active tab is
        // unchanged; a different restored active tab brings ITS view).
        ...projectActiveView(nextActive, nextTabs),
        selectedTypeName: hydratedTypeName,
        dirtyPaths: computeDirty(base, files),
      };
      notify();
      // Cold (tabs reseeded / snapshot-replaced) = a workspace replacement;
      // a warm preserveTabs re-hydrate is the same workspace refreshed.
      emit({ type: "reload", workspaceReplaced: !preserveTabs });
      // Don't re-persist a snapshot we just loaded; persistence resumes on edit.
    },

    setActiveFile(path) {
      if (path === null) return; // closing the editor is closeTab, not this
      this.openTab(specForPath(path));
    },

    setPanelKindResolver(resolve) {
      resolvePanelKind = resolve;
    },
    openTab(spec) {
      // File tabs carry preview semantics (single-click = transient preview
      // that reuses one slot; pin = persistent). Handle that here, then fall
      // through to the generic open/activate for every other tab kind.
      if (spec.kind === "file") {
        const id = spec.path;
        const existing = state.tabs.find((t) => t.id === id);
        if (spec.preview) {
          if (!existing) {
            // Reuse the current preview file tab's slot, else append a new one.
            const idx = state.tabs.findIndex(
              (t) => t.kind === "file" && t.state.preview,
            );
            const tab = makeTab({ kind: "file", path: spec.path, preview: true });
            const tabs =
              idx === -1
                ? [...state.tabs, tab]
                : state.tabs.map((t, i) => (i === idx ? tab : t));
            applyTabs(tabs, id);
          } else {
            applyTabs(state.tabs, id); // already open — just activate
          }
          schedulePersist();
        } else {
          // Pin: open (persistent) or clear the preview flag on the existing tab.
          const wasPreview = existing?.kind === "file" && existing.state.preview;
          if (existing && !wasPreview && state.activeTabId === id) {
            return id; // already pinned + active — true no-op
          }
          const tabs = !existing
            ? [...state.tabs, makeTab({ kind: "file", path: spec.path })]
            : wasPreview
              ? state.tabs.map((t): Tab =>
                  t.id === id && t.kind === "file"
                    ? { ...t, state: { ...t.state, preview: false } }
                    : t,
                )
              : state.tabs;
          applyTabs(tabs, id);
          schedulePersist();
        }
        return id;
      }

      // The custom (plugin) arm derives its id + tab through the panelKind
      // resolver; every other kind is deterministic. Dedup-and-activate is the
      // generic path for both.
      const id = tabIdForSpec(spec, resolvePanelKind);
      const existing = state.tabs.find((t) => t.id === id);
      // The doc tab is a singleton — opening any document RE-POINTS it (path +
      // section anchor) so switching docs is fast and never piles up tabs.
      const tabs =
        spec.kind === "doc"
          ? existing
            ? state.tabs.map((t): Tab =>
                t.id === id && t.kind === "doc"
                  ? { ...t, state: { path: spec.path, slug: spec.slug } }
                  : t,
              )
            : [...state.tabs, makeTab(spec, resolvePanelKind)]
          : existing
            ? state.tabs
            : [...state.tabs, makeTab(spec, resolvePanelKind)];
      applyTabs(tabs, id);
      return id;
    },

    activateTab(id) {
      if (id === state.activeTabId) return;
      if (!state.tabs.some((t) => t.id === id)) return;
      applyTabs(state.tabs, id);
      const tab = state.tabs.find((t) => t.id === id);
      if (tab?.kind === "file") schedulePersist();
    },


    closeTab(id) {
      // The Overview board is the pinned home view on desktop — non-closable.
      // On mobile (single-tab mode) there is no tab strip; it's reopened from
      // the nav, so closing/replacing is allowed there.
      if (id === OVERVIEW_TAB_ID && !singleTab) return;
      const idx = state.tabs.findIndex((t) => t.id === id);
      if (idx === -1) return;
      let tabs = state.tabs.filter((t) => t.id !== id);
      let activeTabId = state.activeTabId;
      if (activeTabId === id) {
        // Activate the left neighbour, else the one now at idx (was the right
        // neighbour), else nothing.
        activeTabId = tabs[idx - 1]?.id ?? tabs[idx]?.id ?? null;
      }
      // Never end up with no tabs: closing the last one re-opens a fresh
      // overview board (the workspace's home view).
      if (tabs.length === 0) {
        const overview = makeTab({ kind: "board" });
        tabs = [overview];
        activeTabId = overview.id;
      }
      applyTabs(tabs, activeTabId);
      // A file tab close is also a file-overlay change (openTabs shrank).
      schedulePersist();
    },

    reorderTabs(from, to) {
      const fromIdx = state.tabs.findIndex((t) => t.id === from);
      const toIdx = state.tabs.findIndex((t) => t.id === to);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
      const tabs = [...state.tabs];
      const [moved] = tabs.splice(fromIdx, 1);
      tabs.splice(toIdx, 0, moved);
      applyTabs(tabs, state.activeTabId);
      schedulePersist();
    },

    duplicateTab(id) {
      const idx = state.tabs.findIndex((t) => t.id === id);
      if (idx === -1) return null;
      const src = state.tabs[idx]!;
      // The doc tab is a SINGLETON — a clone breaks every "the one doc tab"
      // invariant (and a later doc→file rename would convert both clones to
      // the same path id: duplicate-id corruption). Refuse at the store
      // level; the TabStrip UI never offers it, but the API must hold too.
      if (src.kind === "doc") return null;
      // Unique id derived from the source so dedup-by-spec still finds the
      // ORIGINAL (a later openTab/cmd-click activates it, not the clone).
      let n = 2;
      // NUL separator — collision-PROOF against real paths ('#' is legal in
      // file names; a `a.arch#2`-shaped clone id swallowed a same-named
      // file's openTab, and any string-shape predicate stayed ambiguous).
      // NUL can't appear in a path, so a clone id never equals a file path.
      const taken = (candidate: string): boolean =>
        state.tabs.some((t) => t.id === candidate);
      let newId = `${src.id}\u0000${n}`;
      while (taken(newId)) newId = `${src.id}\u0000${++n}`;
      // Clone per-tab state (incl. a fresh viewport object) so the copy's camera
      // diverges from the source's.
      const clonedState = { ...src.state } as { viewport?: BoardViewport | null };
      if (clonedState.viewport) clonedState.viewport = { ...clonedState.viewport };
      const clone = { ...src, id: newId, state: clonedState } as Tab;
      // Clone the source's VIEW record too, so the copy starts at the same
      // selection/aspect but then diverges independently (a fresh object).
      const srcView = views.get(id);
      if (srcView) views.set(newId, { ...srcView });
      const tabs = [...state.tabs.slice(0, idx + 1), clone, ...state.tabs.slice(idx + 1)];
      applyTabs(tabs, newId);
      schedulePersist();
      return newId;
    },

    closeOtherTabs(id) {
      if (!state.tabs.some((t) => t.id === id)) return;
      // Keep the pinned Overview alive on desktop even when closing others.
      const tabs = state.tabs.filter(
        (t) => t.id === id || (!singleTab && t.id === OVERVIEW_TAB_ID),
      );
      if (tabs.length === state.tabs.length) return;
      applyTabs(tabs, id);
      schedulePersist();
    },

    updateTabState(id, partial) {
      const tab = state.tabs.find((t) => t.id === id);
      if (!tab) return;
      const tabs = state.tabs.map((t) =>
        t.id === id ? ({ ...t, state: { ...t.state, ...partial } } as Tab) : t,
      );
      state = { ...state, tabs };
      // Per-tab camera (pan/zoom) updates at 60fps — DON'T notify on those or
      // every subscriber re-renders per frame. But a focal-path change (diving
      // into / out of a space) is rare and the tab strip names the board after
      // its focal space, so notify on that so the label updates.
      // board / file / view tabs all carry a per-tab `viewport` whose focalPath
      // must be compared against the incoming one — else a constant (non-empty)
      // focalPath re-fires notify() on every 60fps pan frame (a whole-app
      // re-render riding every gesture frame; the FullArchitecture-view lag).
      const prevFocal =
        tab.kind === "board" || tab.kind === "file" || tab.kind === "view"
          ? tab.state.viewport?.focalPath
          : undefined;
      const nextVp = (partial as { viewport?: BoardViewport | null }).viewport;
      const nextFocal = nextVp?.focalPath;
      const focalChanged =
        "viewport" in partial &&
        ((prevFocal?.length ?? 0) !== (nextFocal?.length ?? 0) ||
          !!prevFocal?.some((seg, i) => seg !== nextFocal?.[i]));
      if (focalChanged) notify();
      scheduleViewPersist();
    },

    reconcileCustomTabs(removed) {
      const recon = reconcileCustomTabs(
        state.tabs,
        state.activeTabId,
        removed,
        resolvePanelKind,
      );
      if (recon.tabs === state.tabs) return; // nothing closed
      applyTabs(recon.tabs, recon.activeTabId);
      schedulePersist();
    },

    revert(path) {
      if (!state.dirtyPaths.has(path)) return;
      const files = new Map(state.files);
      const baseContent = state.base.get(path);
      const event: WorkspaceEvent =
        baseContent === undefined
          ? { type: "fileRemoved", path } // it was a local-only addition
          : { type: "fileChanged", path, content: baseContent };
      if (baseContent === undefined) files.delete(path);
      else files.set(path, baseContent);
      // A local-only addition reverts to "gone"; if it was ON SCREEN (active
      // FILE tab), re-pick a neighbour — a background revert must not steal
      // the perspective.
      const openFile =
        baseContent === undefined && activeFileTabPath() === path
          ? (pickReplacementFile(state.openTabs, files, path) ?? undefined)
          : undefined;
      commitFiles(files, event, { openFile });
    },

    revertAll() {
      if (state.dirtyPaths.size === 0) return;
      const wasDirty = [...state.dirtyPaths];
      const files = new Map(state.base);
      // Local-only additions vanish on revert; if the file ON SCREEN (active
      // FILE tab) is one of them, open a neighbour in its place. Background
      // vanishing files just drop their tabs — the perspective stays put.
      let openFile: string | undefined;
      const onScreen = activeFileTabPath();
      if (onScreen !== null && !files.has(onScreen)) {
        openFile = pickReplacementFile(state.openTabs, files, onScreen) ?? undefined;
      }
      let tabs = state.tabs;
      let activeTabId = state.activeTabId;
      if (openFile !== undefined) {
        ({ tabs, id: activeTabId } = ensurePathTab(tabs, openFile));
      }
      const recon = reconcileFileTabs(tabs, files, activeTabId);
      pruneViews(recon.tabs);
      state = {
        ...state,
        files,
        tabs: recon.tabs,
        activeTabId: recon.activeTabId,
        ...deriveMirrors(recon.tabs, recon.activeTabId),
        ...projectActiveView(recon.activeTabId, recon.tabs),
        dirtyPaths: new Set(),
      };
      notify();
      // Emit a delta per previously-dirty path so listeners resync to base.
      for (const path of wasDirty) {
        const content = files.get(path);
        emit(
          content === undefined
            ? { type: "fileRemoved", path }
            : { type: "fileChanged", path, content },
        );
      }
      schedulePersist();
    },

    markSaved(paths) {
      const targets = paths ?? [...state.dirtyPaths];
      if (targets.length === 0) return;
      const base = new Map(state.base);
      for (const path of targets) {
        if (state.files.has(path)) base.set(path, state.files.get(path)!);
        else base.delete(path);
      }
      state = { ...state, base, dirtyPaths: computeDirty(base, state.files) };
      notify();
    },

    select(next) {
      // `state.selection` mirrors the ACTIVE tab's view record; write both.
      const cur = state.selection;
      const merged: WorkspaceSelection = { ...cur, ...next };
      if (selectionEquals(merged, cur)) return;
      // Changing the active process (within the tab) resets its scrubber.
      // blastRadius clears on EVERY selection change (the slice's contract) —
      // select() is the board-click / nav-click dispatch, and omitting the
      // reset here left module A's highlight painted around a freshly
      // selected B (r49); applySelection/goBack carry the same reset.
      const patch: Partial<TabViewState> =
        merged.activeProcessId !== cur.activeProcessId
          ? { selection: merged, blastRadius: EMPTY_BLAST, process: { stepIndex: null, autoPlay: false } }
          : { selection: merged, blastRadius: EMPTY_BLAST };
      commitActiveView(patch);
      notify();
      scheduleViewPersist();
    },

    applySelection(next, source) {
      const prev = state.selection;
      const merged: WorkspaceSelection = {
        moduleId: next.moduleId,
        edgeId: next.edgeId,
        interfaceKey: next.interfaceKey ?? null,
        activeProcessId:
          next.activeProcessId === undefined ? prev.activeProcessId : next.activeProcessId,
      };
      if (selectionEquals(merged, prev)) return;
      const navHistory =
        source === "sidebar"
          ? [
              ...state.navHistory,
              {
                selection: prev,
                viewport: viewportSeam?.getViewport() ?? lastViewport,
              },
            ].slice(-MAX_NAV_HISTORY)
          : // A board pick IS the new "main view" the user can later back
            // out to — the old drill trail no longer applies.
            EMPTY_HISTORY;
      // Changing the active process resets its scrubber position.
      const patch: Partial<TabViewState> =
        merged.activeProcessId !== prev.activeProcessId
          ? {
              selection: merged,
              navHistory,
              blastRadius: EMPTY_BLAST,
              process: { stepIndex: null, autoPlay: false },
            }
          : { selection: merged, navHistory, blastRadius: EMPTY_BLAST };
      commitActiveView(patch);
      notify();
      scheduleViewPersist();
    },

    goBack() {
      const stack = state.navHistory;
      if (stack.length === 0) return;
      const entry = stack[stack.length - 1]!;
      // Back-nav restoring a DIFFERENT active process resets its scrubber —
      // the same "new walk" contract select()/applySelection() carry; without
      // it the outgoing process's step position + auto-play were applied to
      // the restored one (r50). Same-process goBack keeps the position.
      const processChanged = entry.selection.activeProcessId !== state.selection.activeProcessId;
      commitActiveView({
        selection: entry.selection,
        navHistory: stack.slice(0, -1),
        blastRadius: EMPTY_BLAST,
        ...(processChanged ? { process: { stepIndex: null, autoPlay: false } } : {}),
      });
      notify();
      if (entry.viewport) viewportSeam?.restoreViewport(entry.viewport);
      scheduleViewPersist();
    },

    setBlastRadius(ids) {
      if (ids === state.blastRadius) return;
      if (ids.length === 0 && state.blastRadius.length === 0) return;
      commitActiveView({ blastRadius: ids });
      notify();
    },
    setSelectedTypeName(name) {
      // Mirror the pick onto the active types tab's state — that's what the
      // persisted snapshot restores from (`deriveViewSelectionFromTab`); the
      // global slice alone would restore as "types perspective, no type".
      // The retag must happen even when the GLOBAL slice already holds `name`
      // (re-picking the same type on a fresh tab whose state is still null).
      // Highlight-only: retags the ACTIVE types tab (that's what persistence
      // restores from) but never arms a pending pick — a single-click nav
      // highlight must not hijack whichever types tab activates later. Open
      // intent goes through `openType`, which arms the pick itself.
      const active = state.tabs.find((t) => t.id === state.activeTabId);
      const retag = active?.kind === "types" && active.state.typeName !== name;
      if (name === state.selectedTypeName && !retag) return;
      const tabs = retag
        ? (state.tabs.map((t) =>
            t === active ? { ...t, state: { ...t.state, typeName: name } } : t,
          ) as typeof state.tabs)
        : state.tabs;
      state = { ...state, tabs, selectedTypeName: name };
      notify();
      if (retag) scheduleViewPersist();
    },
    openType(name) {
      const active = state.tabs.find((t) => t.id === state.activeTabId);
      if (active?.kind === "types") {
        // Already on a types tab — retag it in place (setMode would no-op).
        this.setSelectedTypeName(name);
        return;
      }
      // Arm the pick for the activation setMode triggers (fresh tab or an
      // existing background one — applyTabs adopts + retags, then clears).
      pendingTypePick = name;
      this.setSelectedTypeName(name); // immediate paint of the global slice
      this.setMode("types");
      // The activation consumes the pick synchronously; if setMode could not
      // activate a types tab at all, don't leave it armed for a later,
      // unrelated activation to adopt.
      pendingTypePick = null;
    },

    setSelectedFile(path) {
      if (path === state.selectedFile) return;
      state = { ...state, selectedFile: path };
      notify();
    },

    clearSelection() {
      if (
        selectionEquals(state.selection, EMPTY_SELECTION) &&
        state.blastRadius.length === 0
      ) {
        return;
      }
      commitActiveView({ selection: EMPTY_SELECTION, blastRadius: EMPTY_BLAST });
      notify();
      scheduleViewPersist();
    },

    requestNavReveal(key) {
      state = {
        ...state,
        navReveal: { key, nonce: (state.navReveal?.nonce ?? 0) + 1 },
      };
      notify();
    },

    requestReveal({ path, line = 1, column = 1, focus = false }) {
      state = {
        ...state,
        reveal: {
          path,
          line,
          column,
          focus,
          nonce: (state.reveal?.nonce ?? 0) + 1,
        },
      };
      notify();
    },

    setViewportSeam(seam) {
      viewportSeam = seam;
      return () => {
        // Only clear if a later board hasn't already replaced us.
        if (viewportSeam === seam) viewportSeam = null;
      };
    },

    setSingleTab(on) {
      // A flag, not a list mutation: `state.tabs` stays the full logical set
      // either way — rendering shows only the active tab while on. (See the
      // `singleTab` declaration for why the collapse-to-[active] design died.)
      singleTab = on;
    },

    setMode(mode) {
      // The perspective IS the active tab's kind — switching it just activates
      // (or creates) a tab of that kind. No stored `mode`; activateTab/openTab
      // below are no-ops when that tab is already active.
      const activeTab = state.tabs.find((t) => t.id === state.activeTabId);
      if (mode === modeForTab(activeTab)) {
        // Already on a tab of this perspective — re-activate the canonical one
        // (e.g. focusing the board) but don't churn if it's the active tab.
        const id = tabIdForMode(mode, state.tabs, state.activeTabId);
        if (id && id !== state.activeTabId) this.activateTab(id);
        return;
      }
      const existingId = tabIdForMode(mode, state.tabs, state.activeTabId);
      if (existingId) {
        this.activateTab(existingId);
        return;
      }
      // A doc tab needs a path (it's opened via `openTab({kind:"doc",path})` from
      // the nav/deep-link), so a bare `setMode("docs")` with none open is a no-op
      // — the Documents NAV list switches without forcing a center perspective.
      if (mode === "docs") return;
      // A process tab IS its process (`process:${id}`), so a setMode with no
      // process selected has no tab to make — minting an empty-id one produced
      // a dead tab (no title, the empty "Pick a process" prompt) that no later
      // open could heal, since every real open mints its OWN id. Same no-op
      // contract as the `docs` arm above: the nav keeps its process list.
      if (mode === "process" && state.selection.activeProcessId === null) return;
      // No tab of that kind yet — create a singleton view tab. A process tab
      // carries the entity identity of the selected process.
      const spec: TabSpec =
        mode === "process"
          ? { kind: "process", processId: state.selection.activeProcessId! }
          : mode === "types"
            ? { kind: "types" }
            : { kind: "board" };
      this.openTab(spec);
    },

    setAspectColorKey(key) {
      const aspect = state.aspect;
      if (aspect.colorKey === key) return;
      // Hover/pin named values of the previous key — meaningless now.
      commitActiveView({
        aspect: { ...aspect, colorKey: key, legendHover: null, legendPinned: null },
      });
      notify();
    },

    setLegendHover(value) {
      if (state.aspect.legendHover === value) return;
      commitActiveView({ aspect: { ...state.aspect, legendHover: value } });
      notify();
    },

    setLegendPinned(value) {
      if (state.aspect.legendPinned === value) return;
      commitActiveView({ aspect: { ...state.aspect, legendPinned: value } });
      notify();
    },

    toggleWebKey(key) {
      const webKeys = new Set(state.aspect.webKeys);
      if (!webKeys.delete(key)) webKeys.add(key);
      commitActiveView({ aspect: { ...state.aspect, webKeys } });
      notify();
    },

    setWebKeys(keys) {
      const prev = state.aspect.webKeys;
      const prevRep = state.aspect.replicatedKeys;
      // A replicated key that just left the web set can't stay replicated.
      const rep = new Set([...prevRep].filter((k) => keys.has(k)));
      const repChanged = rep.size !== prevRep.size;
      if (!repChanged && keys.size === prev.size && [...keys].every((k) => prev.has(k))) return;
      commitActiveView({ aspect: { ...state.aspect, webKeys: new Set(keys), replicatedKeys: rep } });
      notify();
    },

    setAspectMode(key, mode) {
      const webKeys = new Set(state.aspect.webKeys);
      const replicatedKeys = new Set(state.aspect.replicatedKeys);
      if (mode === "off") {
        webKeys.delete(key);
        replicatedKeys.delete(key);
      } else {
        webKeys.add(key);
        if (mode === "replicated") replicatedKeys.add(key);
        else replicatedKeys.delete(key);
      }
      commitActiveView({ aspect: { ...state.aspect, webKeys, replicatedKeys } });
      notify();
    },

    setProcessStep(idx) {
      const cur = state.process;
      const next = typeof idx === "function" ? idx(cur.stepIndex) : idx;
      if (next === cur.stepIndex) return;
      commitActiveView({ process: { ...cur, stepIndex: next } });
      notify();
      scheduleViewPersist();
    },

    setProcessAutoPlay(on) {
      if (on === state.process.autoPlay) return;
      commitActiveView({ process: { ...state.process, autoPlay: on } });
      notify();
      scheduleViewPersist();
    },

    reportViewport(vp) {
      lastViewport = vp;
      const prev = state.focalPath;
      const samePath =
        vp.focalPath.length === prev.length && vp.focalPath.every((id, i) => id === prev[i]);
      if (!samePath) {
        state = { ...state, focalPath: vp.focalPath };
        notify();
      }
      scheduleViewPersist();
    },

    getLastViewport: () => lastViewport,

    getViews: () => [...views.entries()].map(([id, view]) => ({ id, view })),
    getLiveTabIds: () => state.tabs.map((t) => t.id),
    getLogicalTabs: () => state.tabs,

    getBuilderConfig: (tabId) => viewFor(tabId).builderConfig,

    setBuilderConfig(tabId, config) {
      if (viewFor(tabId).builderConfig === config) return;
      commitTabView(tabId, { builderConfig: config });
      notify();
      scheduleViewPersist();
    },

    hover: createHoverStore(),

    emit,
    on(handler) {
      eventListeners.add(handler);
      return () => eventListeners.delete(handler);
    },

    flush: flushAll,

    destroy() {
      // Flush pending saves so a REAL unmount/close doesn't drop the last
      // edit, then release the timers. (Subscriber/event listeners are
      // released by their own unsubscribe.)
      flushAll();
      viewportSeam = null;
    },
  };
}

// --- React bindings --------------------------------------------------------
// Slice hooks — each re-renders only on its own slice.

export function useWorkspaceState(store: WorkspaceStore): WorkspaceState {
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}
export function useWorkspaceFiles(store: WorkspaceStore): Vfs {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().files,
    () => store.getState().files,
  );
}
export function useActiveFile(store: WorkspaceStore): string | null {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().activeFile,
    () => store.getState().activeFile,
  );
}
export function useReveal(store: WorkspaceStore): WorkspaceState["reveal"] {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().reveal,
    () => store.getState().reveal,
  );
}
export function useWorkspaceSelection(store: WorkspaceStore): WorkspaceSelection {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().selection,
    () => store.getState().selection,
  );
}
export function useDirtyPaths(store: WorkspaceStore): ReadonlySet<string> {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().dirtyPaths,
    () => store.getState().dirtyPaths,
  );
}
export function useOpenTabs(store: WorkspaceStore): readonly string[] {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().openTabs,
    () => store.getState().openTabs,
  );
}
export function useTabs(store: WorkspaceStore): readonly Tab[] {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().tabs,
    () => store.getState().tabs,
  );
}
export function useActiveTabId(store: WorkspaceStore): string | null {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().activeTabId,
    () => store.getState().activeTabId,
  );
}
/**
 * Whether the SOURCE takes writes back.
 *
 * Not "may the user type": a readonly source still edits locally, as a dirty
 * overlay against its pristine base. It is the answer to "does changing this
 * reach the files" — which is what decides whether an editing affordance is
 * honest or a dead end.
 */
export function useSourceWritable(store: WorkspaceStore): boolean {
  return useSyncExternalStore(
    store.subscribe,
    () => !store.getState().readonly,
    () => !store.getState().readonly,
  );
}

export function useActiveTabKind(store: WorkspaceStore): TabKind | null {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().tabs.find((t) => t.id === store.getState().activeTabId)?.kind ?? null,
    () => store.getState().tabs.find((t) => t.id === store.getState().activeTabId)?.kind ?? null,
  );
}
export function useWorkspaceAspect(store: WorkspaceStore): WorkspaceAspectState {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().aspect,
    () => store.getState().aspect,
  );
}
/** The active tab's process-scrubber position (per-tab). */
export function useWorkspaceProcess(
  store: WorkspaceStore,
): WorkspaceState["process"] {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().process,
    () => store.getState().process,
  );
}
export function useBlastRadius(store: WorkspaceStore): readonly string[] {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().blastRadius,
    () => store.getState().blastRadius,
  );
}
export function useSelectedTypeName(store: WorkspaceStore): string | null {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedTypeName,
    () => store.getState().selectedTypeName,
  );
}
export function useSelectedFile(store: WorkspaceStore): string | null {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedFile,
    () => store.getState().selectedFile,
  );
}
/** Drill-down back-stack; `canGoBack` is just `length > 0`. */
export function useNavHistory(store: WorkspaceStore): readonly SelectionHistoryEntry[] {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().navHistory,
    () => store.getState().navHistory,
  );
}
export function useFocalPath(store: WorkspaceStore): readonly string[] {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().focalPath,
    () => store.getState().focalPath,
  );
}
